import { generatePath, useHistory } from "react-router-dom";
import { TaxFormSubmissionViewFilters } from "../../query/search/taxForm/queries/filtering";
import { toURLQuerySearch } from "../../shared/hooks/useUrlQuery";
import { useParams } from "react-router";
import { useCurrentTaxFilingYear } from "./utils/useCurrentTaxFilingYear";
import { getCurrentTaxFilingYear } from "./utils/getCurrentTaxFilingYear";
import isPlainObject from "lodash/isPlainObject";

export const TAX_FILING_PATH = "/member/tax-filing/:year";
export const TAX_FILING_1099_FILING_PATH = TAX_FILING_PATH + "/1099-filing";
export const TAX_FILING_1099_FILING_DASHBOARD_PATH =
  TAX_FILING_1099_FILING_PATH + "/dashboard";
export const TAX_FILING_1099_FILING_DASHBOARD_SEARCH_PATH =
  TAX_FILING_1099_FILING_DASHBOARD_PATH + "/search";

export const TAX_FILING_1099_FILING_DASHBOARD_TABS_PATH =
  TAX_FILING_1099_FILING_DASHBOARD_PATH +
  "/:path(all|needs-action|ready|submitted|excluded)";

export const TAX_FILING_1099_FILING_GENERATE_TAX_FORMS_INIT_PATH =
  TAX_FILING_1099_FILING_PATH + "/generate-tax-forms";

export const TAX_FILING_1099_FILING_GENERATE_TAX_FORMS_DETAILS_PATH =
  TAX_FILING_1099_FILING_GENERATE_TAX_FORMS_INIT_PATH + "/:bulkBatchId";

export const TAX_FILING_1099_FILING_GENERATE_TAX_FORMS_PROGRESS_PATH =
  TAX_FILING_1099_FILING_GENERATE_TAX_FORMS_DETAILS_PATH + "/progress";

export const TAX_FILING_1099_FILING_TAX_FORM_DETAILS_PATH =
  TAX_FILING_1099_FILING_PATH + "/details/:taxFormId/:submissionId";

export const TAX_FILING_1099_FILING_TAX_FORM_DETAILS_NEW_CORRECTION_PATH =
  TAX_FILING_1099_FILING_TAX_FORM_DETAILS_PATH + "/new-correction";

export const TAX_FILING_1099_FILING_TAX_FORM_DETAILS_REVIEW_CORRECTION_PATH =
  TAX_FILING_1099_FILING_TAX_FORM_DETAILS_PATH + "/review-correction";

export const TAX_FILING_1099_FILING_TAX_FORM_DETAILS_VIEW_CORRECTION_PATH =
  TAX_FILING_1099_FILING_TAX_FORM_DETAILS_PATH + "/view-correction";

export const TAX_FILING_1099_FILING_TAX_FORM_DETAILS_CONTRACTOR_EDIT_PATH =
  TAX_FILING_1099_FILING_TAX_FORM_DETAILS_PATH + "/edit-contractor";

export const TAX_FILING_1099_FILING_TAX_FORM_DETAILS_AMOUNT_EDIT_PATH =
  TAX_FILING_1099_FILING_TAX_FORM_DETAILS_PATH + "/edit-amount";

export const TAX_FILING_1099_FILING_TAX_FORM_DETAILS_HISTORY =
  TAX_FILING_1099_FILING_TAX_FORM_DETAILS_PATH + "/history";

export const TAX_FILING_1099_FILING_TAX_FORM_DETAILS_PREVIEW_1099_NEC_PATH =
  TAX_FILING_1099_FILING_TAX_FORM_DETAILS_PATH + "/preview-1099-nec";

export const TAX_FILING_1099_FILING_PREVIEW_PATH =
  TAX_FILING_1099_FILING_PATH + "/preview";
export const TAX_FILING_1099_FILING_SETTINGS_PATH =
  TAX_FILING_1099_FILING_PATH + "/settings";
export const TAX_FILING_1099_FILING_UPDATE_SUPPORT_INFORMATION_PATH =
  TAX_FILING_1099_FILING_SETTINGS_PATH + "/support-information";
export const TAX_FILING_1099_FILING_UPDATE_STATE_FILING_SETTINGS_PATH =
  TAX_FILING_1099_FILING_SETTINGS_PATH + "/state-filing";
export const TAX_FILING_1099_FILING_UPDATE_CALCULATION_SETTINGS_PATH =
  TAX_FILING_1099_FILING_SETTINGS_PATH + "/calculation";

export const TAX_FILING_1099_FILING_SUBMISSION_PATH =
  TAX_FILING_1099_FILING_PATH + "/submission";
export const TAX_FILING_1099_FILING_SUBMISSION_EDIT_PAYER_INFO_PATH =
  TAX_FILING_1099_FILING_SUBMISSION_PATH + "/edit-payer-information";
export const TAX_FILING_1099_FILING_SUBMISSION_EDIT_STATE_FILING_PATH =
  TAX_FILING_1099_FILING_SUBMISSION_PATH + "/edit-state-filing";

export const TAX_FILING_1099_DASHBOARD_TABS = [
  "all",
  "needs-action",
  "ready",
  "submitted",
  "excluded"
] as const;

export type TaxFiling1099TabSlug =
  (typeof TAX_FILING_1099_DASHBOARD_TABS)[number];

export const useTaxFilingYearPath = (path: string) => {
  return generatePath(path, { year: useCurrentTaxFilingYear() });
};

export const useGetTaxFilingTabPath = () => {
  return (slug: TaxFiling1099TabSlug) =>
    generatePath(TAX_FILING_1099_FILING_DASHBOARD_TABS_PATH, {
      year: getCurrentTaxFilingYear(),
      path: slug
    });
};

export const useGetTaxFilingYearPath = () => {
  return (path: string) =>
    generatePath(path, { year: getCurrentTaxFilingYear() });
};

export const useGoToTaxFilingPath = (path: string) => {
  const history = useHistory();

  return (params?: Record<string, string | number>) => {
    const pathname = generatePath(path, {
      year: getCurrentTaxFilingYear(),
      ...(params || {})
    });

    history.push(pathname);
  };
};

export const useGoToTaxFormDetails = () => {
  const goTo = useGoToTaxFormSubPath(
    TAX_FILING_1099_FILING_TAX_FORM_DETAILS_PATH
  );

  return (taxFormId: string, submissionId: string, year?: number) => {
    goTo(taxFormId, submissionId, year);
  };
};

const useGoToTaxFormSubPath = (path: string) => {
  const history = useHistory();

  return (taxFormId: string, submissionId: string, year?: number) => {
    const url = generatePath(path, {
      year: year || getCurrentTaxFilingYear(),
      taxFormId,
      submissionId
    });

    history.push(url);
  };
};

export const useTaxFormSubmissionParamsIds = (): {
  taxFormId: string;
  submissionId: string;
} => {
  const { taxFormId, submissionId } = useParams<{
    taxFormId: string;
    submissionId: string;
  }>();

  return {
    taxFormId,
    submissionId
  };
};

export const useGoToTaxFormReviewCorrection = () =>
  useGoToTaxFormSubPath(
    TAX_FILING_1099_FILING_TAX_FORM_DETAILS_REVIEW_CORRECTION_PATH
  );

export const useGoToTaxFormViewCorrection = () =>
  useGoToTaxFormSubPath(
    TAX_FILING_1099_FILING_TAX_FORM_DETAILS_VIEW_CORRECTION_PATH
  );

export const useGoToTaxFormNewCorrection = () =>
  useGoToTaxFormSubPath(
    TAX_FILING_1099_FILING_TAX_FORM_DETAILS_NEW_CORRECTION_PATH
  );

export const useGoToTaxFormEditContractor = () =>
  useGoToTaxFormSubPath(
    TAX_FILING_1099_FILING_TAX_FORM_DETAILS_CONTRACTOR_EDIT_PATH
  );

export const useGoToTaxForm1099NECPreview = () =>
  useGoToTaxFormSubPath(
    TAX_FILING_1099_FILING_TAX_FORM_DETAILS_PREVIEW_1099_NEC_PATH
  );

export const useGoToTaxFormHistory = () =>
  useGoToTaxFormSubPath(TAX_FILING_1099_FILING_TAX_FORM_DETAILS_HISTORY);

export const useGoToTaxFormEditAmount = () =>
  useGoToTaxFormSubPath(
    TAX_FILING_1099_FILING_TAX_FORM_DETAILS_AMOUNT_EDIT_PATH
  );

export const useGoToGenerateTaxFormDetails = () => {
  const history = useHistory();

  return (bulkBatchId: string) => {
    history.push(
      generatePath(TAX_FILING_1099_FILING_GENERATE_TAX_FORMS_DETAILS_PATH, {
        bulkBatchId,
        year: getCurrentTaxFilingYear()
      })
    );
  };
};

export const useGoToGenerateTaxFormProgress = () => {
  const history = useHistory();

  return (bulkBatchId: string) => {
    history.push(
      generatePath(TAX_FILING_1099_FILING_GENERATE_TAX_FORMS_PROGRESS_PATH, {
        bulkBatchId,
        year: getCurrentTaxFilingYear()
      })
    );
  };
};

export const useGoToSubmissionTaxFormProgress = () => {
  const history = useHistory();

  return (bulkBatchId: string) => {
    const path = `${TAX_FILING_1099_FILING_SUBMISSION_PATH}/submitting/:bulkBatchId`;

    const pathname = generatePath(path, {
      year: getCurrentTaxFilingYear(),
      bulkBatchId
    });

    history.push(pathname);
  };
};

export const useGoToTaxFilingDashboard = () => {
  const history = useHistory();
  const path = useTaxFilingYearPath(TAX_FILING_1099_FILING_DASHBOARD_PATH);

  return (filters?: TaxFormSubmissionViewFilters) => {
    const search =
      filters && isPlainObject(filters)
        ? toURLQuerySearch({ filter: filters })
        : "";

    history.push({
      pathname: path + "/all",
      search
    });
  };
};
